import { Typography } from '@forethought-technologies/forethought-elements';
import Box from '@mui/material/Box';
import React, { useMemo, useEffect, useRef } from 'react';
import { IconClockHour2 } from '@tabler/icons-react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { styled, useTheme } from '@mui/material/styles';
import { selectTabName } from 'reducers/config';
import {
  ForethoughtLiveChatHandoffComponent,
  SuncoLiveChatComponent,
  isForethoughtLiveChatHandoffComponent,
} from 'api/types';
import {
  selectLiveChatAgentIcon,
  selectLiveChatAgentName,
  selectLiveChatStatus,
  selectQueuePosiition,
} from 'reducers/live-chat';
import {
  selectConversationId,
  updateConversationIdUsedInPreviousSession,
} from 'reducers/workflows';
import {
  avatarPlaceholderBgColors,
  avatarPlaceholderColors,
  getRandomLetter,
} from './utils';
import { Avatar } from '@mui/material';
import useHandleLiveChatSetup from 'components/App/hooks/useHandleLiveChatSetup';
import { LoadingMessageWithDots } from 'components/LoadingMessageWithDots';
import { selectWaitTimeMessage } from 'reducers/live-chat/index';

const NUM_OF_AGENTS_TO_SHOW = 3;

interface Props {
  setStepSent: (query?: string) => void;
  widgetComponent: ForethoughtLiveChatHandoffComponent | SuncoLiveChatComponent;
}

export const ForethoughtLiveChat: React.FC<React.PropsWithChildren<Props>> = ({
  setStepSent,
  widgetComponent,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const companyName = useAppSelector(selectTabName);
  const chatStatus = useAppSelector(selectLiveChatStatus);
  const agentChatIcon = useAppSelector(selectLiveChatAgentIcon);
  const agentName = useAppSelector(selectLiveChatAgentName);
  const conversationId = useAppSelector(selectConversationId);
  const queuePosition = useAppSelector(selectQueuePosiition);
  const waitTimeMessage = useAppSelector(selectWaitTimeMessage);
  const isForethoughtLiveChatHandoff =
    isForethoughtLiveChatHandoffComponent(widgetComponent);
  const forethoughtText = widgetComponent.component_fields.forethought_text;
  const agentsToShow = useMemo(() => {
    const availableAgents = isForethoughtLiveChatHandoff
      ? widgetComponent.component_fields.available_agents
      : [];

    if (availableAgents.length < NUM_OF_AGENTS_TO_SHOW) {
      const numToFill = NUM_OF_AGENTS_TO_SHOW - availableAgents.length;
      const randomAgents = [...Array(numToFill)].map(() => ({
        avatar: null,
        name: getRandomLetter(),
      }));

      return availableAgents.concat(randomAgents);
    }
    return availableAgents;
  }, [widgetComponent.component_fields, isForethoughtLiveChatHandoff]);
  const containerRef = useRef<HTMLDivElement>(null);

  useHandleLiveChatSetup({
    conversationId,
    setStepSent,
    widgetComponent,
  });
  useEffect(() => {
    dispatch(updateConversationIdUsedInPreviousSession(conversationId));
  }, [conversationId, dispatch]);
  useEffect(() => {
    // use setTimeout to scrollIntoView
    // after component has been rendered
    if (chatStatus === 'pending') {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [chatStatus]);

  if (chatStatus === 'failure') {
    return null;
  }

  if (!chatStatus) {
    return <LoadingMessageWithDots />;
  }

  if (chatStatus === 'pending') {
    return (
      <Box
        ref={containerRef}
        sx={{
          alignItems: 'center',
          border: `1px solid ${theme.palette.colors.slate[200]}`,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',

          justifyContent: 'center',
          // 16px less than bottom because of components above
          marginBottom: '36px',
          marginTop: '20px',
          padding: '20px',
        }}
      >
        <Box display='flex'>
          {agentsToShow.map(({ avatar, name }, index) => {
            return (
              <Box key={index} ml={-1}>
                {avatar ? (
                  <AvatarWithBorder
                    alt={name}
                    src={avatar}
                    sx={{
                      height: 48,
                      width: 48,
                    }}
                  />
                ) : (
                  <AvatarWithBorder
                    sx={{
                      bgcolor: avatarPlaceholderBgColors[index],
                      height: 48,
                      width: 48,
                    }}
                  >
                    <Typography
                      color={avatarPlaceholderColors[index]}
                      variant='font20'
                    >
                      {name.charAt(0)}
                    </Typography>
                  </AvatarWithBorder>
                )}
              </Box>
            );
          })}
        </Box>
        <Box>
          <Typography color={theme.palette.colors.grey[600]} variant='font12'>
            {forethoughtText.waiting_for_agent}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <SvgWrapper>
            <IconClockHour2 height={16} />
          </SvgWrapper>
          <Typography variant='font14Bold'>
            {waitTimeMessage
              ? waitTimeMessage
              : widgetComponent.component_fields.placeholder_message}
          </Typography>
        </Box>
        {!!queuePosition && (
          <Box>
            <Typography color={theme.palette.colors.grey[600]} variant='font12'>
              Queue position: {queuePosition}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  if (chatStatus === 'active' || chatStatus === 'inactive') {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          // 16px less than bottom because of components above
          marginBottom: '36px',
          marginTop: '20px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
          <Box
            component='hr'
            sx={{
              alignSelf: 'center',
              border: `1px solid ${theme.palette.colors.grey[200]}`,
              flex: 1,
              height: 0,
            }}
          />
          <Box sx={{ color: theme.palette.colors.grey[600] }}>
            <Typography variant='font12'>
              {companyName} {forethoughtText.agent_connected}
            </Typography>
          </Box>
          <Box
            component='hr'
            sx={{
              alignSelf: 'center',
              border: `1px solid ${theme.palette.colors.grey[200]}`,
              flex: 1,
              height: 0,
            }}
          />
        </Box>
        <Box
          sx={{
            color: theme.palette.colors.grey[600],
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography variant='font12'>
            {forethoughtText.chatting_with_agent}
          </Typography>
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            color: theme.palette.colors.grey[600],
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '16px',
          }}
        >
          <LiveChatAgentIconWrapper>
            {agentChatIcon ? (
              <AvatarWithBorder
                alt={agentName}
                src={agentChatIcon}
                sx={{
                  height: 80,
                  width: 80,
                }}
              />
            ) : (
              <AvatarWithBorder
                sx={{
                  bgcolor: theme.palette.colors.purple[100],
                  height: 80,
                  width: 80,
                }}
              >
                <Typography
                  color={theme.palette.colors.purple[500]}
                  variant='font36'
                >
                  {agentName.charAt(0)}
                </Typography>
              </AvatarWithBorder>
            )}
          </LiveChatAgentIconWrapper>
          <Typography variant='font14Bold'>{agentName}</Typography>
        </Box>
      </Box>
    );
  }

  return null;
};

const LiveChatAgentIconWrapper = styled('div')`
  height: 84px;
  width: 84px;
  border: 2px solid ${props => props.theme.palette.colors.purple[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 42px;
  box-sizing: border-box;
`;

const AvatarWithBorder = styled(Avatar)`
  border: 2px solid ${props => props.theme.palette.colors.white};
  box-sizing: border-box;
`;

const SvgWrapper = styled('span')`
  display: inline-block;
  vertical-align: middle;
  margin-bottom: -2px;
`;
